/**
 * Prefixed ULID identifiers (`usr_01jb3k…`), mirrored from rt_common.ids so the command palette can
 * resolve any id a person pastes. The prefix names the kind; the body is 26 lowercase Crockford chars.
 */

export const ID_PREFIXES = {
  usr: 'person',
  idn: 'identity',
  org: 'organisation',
  ses: 'session',
  svc: 'service account',
  agt: 'agent',
  tok: 'API token',
  dev: 'device',
  chl: 'challenge',
  obj: 'objective',
  msn: 'mission',
  asg: 'assignment',
  ctb: 'contribution',
  art: 'artifact',
  ver: 'artifact version',
  col: 'collection',
  acc: 'ledger account',
  jrn: 'journal entry',
  epo: 'epoch',
  pol: 'reward policy',
  hld: 'hold',
  apr: 'approval',
  stl: 'settlement batch',
  wdr: 'withdrawal',
  ntf: 'notification',
  aud: 'audit entry',
  evt: 'event',
  frd: 'fraud case',
  wal: 'wallet',
  job: 'job',
  skl: 'skill node',
  lnk: 'lineage edge',
  grt: 'access grant',
  rdy: 'readiness attestation',
  cap: 'capture',
  seg: 'segment',
  rev: 'review',
  ds: 'dataset',
  ann: 'annotation',
  scn: 'scan',
  scene: 'scene',
  pkg: 'scene package',
  fb: 'feedback',
  sim: 'sim session',
  eps: 'episode',
  tsk: 'task',
  evl: 'evaluation',
  fail: 'failure case',
} as const

export type IdPrefix = keyof typeof ID_PREFIXES

const BODY = '[0-9a-hjkmnp-tv-z]'
const FULL = new RegExp(`^([a-z][a-z0-9]{1,7})_(${BODY}{26})$`)
const PARTIAL = new RegExp(`^([a-z][a-z0-9]{1,7})_(${BODY}{0,26})$`)

export interface ParsedId {
  prefix: string
  kind: string | null
  body: string
  /** All 26 characters present. */
  complete: boolean
  /** The prefix is registered. */
  known: boolean
  value: string
}

/** Parses `usr_01…` (complete or still being typed). Whitespace and case are forgiven. */
export function parseId(input: string): ParsedId | null {
  const value = input.trim().toLowerCase()
  const m = FULL.exec(value) ?? PARTIAL.exec(value)
  if (!m) return null
  const prefix = m[1]!
  const body = m[2] ?? ''
  const kind = (ID_PREFIXES as Record<string, string>)[prefix] ?? null
  return { prefix, kind, body, complete: body.length === 26, known: kind !== null, value }
}

/** Skill-graph keys (`task:fold_tshirt`) are resolvable too. */
const SKILL_KEY = /^(skill|task|object_category|embodiment|environment|failure_mode):[a-z0-9_.-]+$/

export function parseSkillKey(input: string): { kind: string; slug: string; value: string } | null {
  const value = input.trim().toLowerCase()
  if (!SKILL_KEY.test(value)) return null
  const [kind, slug] = value.split(':') as [string, string]
  return { kind, slug, value }
}

/** `art_01jb3kq9…x4` — keeps the prefix and both ends so ids stay distinguishable in tight columns. */
export function shortId(id: string, head = 6, tail = 4): string {
  const i = id.indexOf('_')
  if (i < 0 || id.length <= i + 1 + head + tail + 1) return id
  return `${id.slice(0, i + 1 + head)}…${id.slice(-tail)}`
}
